import { Navigate } from "react-router";
import { FullscreenLoading } from "@/components";
import {
  type PermissionByRole,
  type PermissionRoleList,
  usePermission,
} from "@/config/permission/permissions";
import { getPermissionCatalogue } from "@/env-variables";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo";
import { useRolePermissionsStatus } from "@/stores/domains/user/userStore";
import { evaluateGuardState } from "./evaluate";

const withPermissionGuard = (
  Component: React.ComponentType,
  requiredPermissions: PermissionRoleList | PermissionByRole | PermissionByRole[],
) => {
  return function PermissionGuarded() {
    const hasHydrated = useAdditionalInfo((state) => state._hasHydrated);
    const role = useAdditionalInfo((state) => state.role);
    const rolePermissionsStatus = useRolePermissionsStatus();
    const { hasPageAccess } = usePermission();

    const permissions = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions];

    const decision = evaluateGuardState({
      catalogue: getPermissionCatalogue(),
      hasHydrated,
      hasRole: role !== null,
      rolePermissionsStatus,
      hasAccess: hasPageAccess(permissions as PermissionRoleList),
    });

    if (decision === "loading") {
      return (
        <FullscreenLoading
          message="Loading permissions..."
          animationType="particles"
          animationSize={70}
          animationColor="#52c41a"
        />
      );
    }

    if (decision === "select-role") {
      return <Navigate to="/additional-info" replace />;
    }

    if (decision === "denied") {
      return <Navigate to="/" replace />;
    }

    return <Component />;
  };
};
export default withPermissionGuard;
